import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';

import {
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  List,
  Button,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  makeStyles,
  IconButton
} from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/Remove';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import Alert from '../common/alert';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { updateTyreAwardOrder } from './tyreAwardsSlice';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const useStyles = makeStyles((theme) => ({
  head: {
    '& .MuiTableCell-head': {
      borderBottomColor: 'rgba(0,0,0,0.6)'
    }
  },
  row: {
    verticalAlign: 'top',
  }
  ,
  magazine: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  magazineLogo: {
    flexBasis: '70px',
    flexGrow: 0,
    paddingRight: theme.spacing(1),

    '& img': {
      maxWidth: '100%'
    }
  }
}));

const rowsPerPageOptions = [25, 50, 75, 100];

const TyreAwardsList = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { itemOwnerId } = useParams();
  const [isOrderingMode, setIsOrderingMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [awardOrder, setAwardOrder] = useState([]);

  const { items, noResultsMessage, rows = 50 } = props;

  //TODO: Get real permissions
  const canEdit = true;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rows);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const cancelOrderChanges = () => {
    setHasUnsavedChanges(false);
    setIsOrderingMode(false);
    setAwardOrder(items);
  };

  useEffect(() => {
    cancelOrderChanges();
  }, [items]);

  const onDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    const editedItems = reorder(
      awardOrder,
      result.source.index,
      result.destination.index
    );

    setHasUnsavedChanges(true);
    setAwardOrder(editedItems);
  };

  const saveOrderChanges = async () => {
    // Save to server
    const res = await dispatch(updateTyreAwardOrder({ itemOwnerId, awards: awardOrder.map((a, idx) => ({ id: a.id, order: idx + 1 })) }));
    console.log(res.payload.awards)
    if (res.payload.awards?.length > 0) {
      dispatch(enqueueSnackbar({
        message: t('tyreAwards:awardOrderUpdated'),
        options: {
          variant: 'success'
        }
      }));
    } else {
      dispatch(enqueueSnackbar({
        message: t('tyreAwards:errorSavingAwardOrder'),
        options: {
          variant: 'error'
        }
      }));
    }
  };

  if (!items || !items.length) {
    if (noResultsMessage) {
      return (
        <Alert severity="info">{noResultsMessage}</Alert>
      );
    } else {
      return null;
    }
  }

  const visibleRows = isOrderingMode ? awardOrder : items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <TableContainer>
      <Table>
        <TableHead className={classes.head}>
          <TableRow>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:product')}</Typography></TableCell>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:magazine')}</Typography></TableCell>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:award')}</Typography></TableCell>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:country')}</Typography></TableCell>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:isPublished')}</Typography></TableCell>
            <TableCell size="small"><Typography variant="overline">{t('tyreAwards:review')}</Typography></TableCell>
            <TableCell size="small"></TableCell>

          </TableRow>
        </TableHead>

        {isOrderingMode ? <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppableFeedItems">
            {provided => (
              <TableBody className={classes.body} ref={provided.innerRef} {...provided.droppableProps}>
                {awardOrder.map((row, index) =>
                  <Draggable key={row.id} draggableId={row.id} index={index}>
                    {provided => (
                      <TableRow key={row.id} classes={{ root: classes.row }} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <TableCell>{row.product}</TableCell>
                        <TableCell width="20%">
                          <div className={classes.magazine}>
                            <div className={classes.magazineLogo}>
                              <img src={row.magazineLogo} alt="" />
                            </div>
                            <div className={classes.magazineNumber}>
                              {row.magazineName}<br />
                              {row.magazineNumber}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{row.award}</TableCell>
                        <TableCell>{row.countryName}</TableCell>
                        <TableCell>{row.published === '1' ? <CheckIcon style={{ color: 'green' }} /> : <RemoveIcon style={{ color: 'red' }} />}</TableCell>
                        <TableCell dangerouslySetInnerHTML={{ __html: row.review }}></TableCell>
                        <TableCell>
                          {isOrderingMode && <DragIndicatorIcon color="primary" />}
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                )}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext> : <TableBody className={classes.body}>
          {visibleRows.map(award => (
            <TableRow key={award.id} classes={{ root: classes.row }}>
              <TableCell>{award.product}</TableCell>
              <TableCell width="20%">
                <div className={classes.magazine}>
                  <div className={classes.magazineLogo}>
                    <img src={award.magazineLogo} alt="" />
                  </div>
                  <div className={classes.magazineNumber}>
                    {award.magazineName}<br />
                    {award.magazineNumber}
                  </div>
                </div>
              </TableCell>
              <TableCell>{award.award}</TableCell>
              <TableCell>{award.countryName}</TableCell>
              <TableCell>{award.published === '1' ? <CheckIcon style={{ color: 'green' }} /> : <RemoveIcon style={{ color: 'red' }} />}</TableCell>
              <TableCell dangerouslySetInnerHTML={{ __html: award.review }}></TableCell>
              <TableCell>
                <IconButton component={Link} to={`/pih/owner/${itemOwnerId}/tyre-award/${award.id}`} aria-label={t('tyreAwards:edit')} title={t('tyreAwards:edit')} color="primary" disabled={!canEdit}>
                  <EditIcon />
                </IconButton>
                {isOrderingMode && <DragIndicatorIcon color="primary" />}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

        }
        <TableFooter>
          <TableRow>
            <TableCell>
              {props.canOrder && <Button
                variant="contained"
                color={isOrderingMode ? 'secondary' : 'primary'}
                disableElevation
                onClick={() => !isOrderingMode ? setIsOrderingMode(!isOrderingMode) : cancelOrderChanges()}
                startIcon={<DragIndicatorIcon />}
                className={classes.newBtn}
              >
                {isOrderingMode ? t('tyreAwards:cancelAwardOrder') : t('tyreAwards:orderAwards')}
              </Button>}
            </TableCell>
            {hasUnsavedChanges && isOrderingMode && <TableCell><Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={saveOrderChanges}
              startIcon={<DragIndicatorIcon />}
              className={classes.newBtn}
            >
              {t('tyreAwards:saveAwardOrder')}
            </Button></TableCell>}
            {!isOrderingMode && <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              colSpan={6}
              count={items.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />}
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
};

TyreAwardsList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  noResultsMessage: PropTypes.string,
  rows: PropTypes.oneOf(rowsPerPageOptions),
  canOrder: PropTypes.bool
};

export default TyreAwardsList;
